// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { OptionGroup } from "../OptionGroup"
import { useI18n } from "@/i18n"
import type { AnswerDraft } from "../types"

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft) => void
}

/** Step 10 (wire) — daily/batch/hybrid planning mode + household servings + ADR-023 snack timing. */
export function Step10CookingStyle({ value, onChange }: Props) {
  const { t } = useI18n()
  const planningMode = (value.planningMode as string | undefined) ?? null
  // 2026-09-02 owner decision: household portions removed from onboarding
  // for the pilot — the field was functionally dead (audit: it never
  // scaled quantities/nutrition/shopping) and implied semantics we don't
  // have. Auto-answered 1 (same ADR-034 idiom as the other auto-answered
  // steps); the backend field stays for future household-aware planning.
  const householdServings = 1
  // ADR-023: starts null (no preselected default) — an explicit choice is
  // required before Continue enables (steps.ts step-10 gating).
  const snackPreference = (value.snackPreference as string | undefined) ?? null

  return (
    <div className="flex flex-col gap-5">
      <OptionGroup
        legend={t("onboarding.step10.title")}
        columns={3}
        value={planningMode}
        onChange={next => onChange({ planningMode: next, householdServings, snackPreference })}
        options={[
          { value: "daily", label: t("onboarding.step10.daily") },
          { value: "batch", label: t("onboarding.step10.batch") },
          { value: "hybrid", label: t("onboarding.step10.hybrid") },
        ]}
      />
      <OptionGroup
        legend={t("onboarding.step10.snackLabel")}
        columns={2}
        value={snackPreference}
        onChange={next => onChange({ planningMode, householdServings, snackPreference: next })}
        options={[
          { value: "none", label: t("onboarding.step10.snackNone") },
          { value: "morning", label: t("onboarding.step10.snackMorning") },
          { value: "afternoon", label: t("onboarding.step10.snackAfternoon") },
          { value: "both", label: t("onboarding.step10.snackBoth") },
        ]}
      />
    </div>
  )
}
